import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiPlus, FiDownload, FiTrash2 } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, confirmDelete, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import StatusBadge from '../../components/common/StatusBadge';
import Loader from '../../components/common/Loader';
import Modal from '../../components/common/Modal';
import { useAuth } from '../../context/AuthContext';
import SearchableSelect from '../../components/common/SearchableSelect';
import { formatMoney, formatDate, today } from '../../utils/format';

export default function MachineDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [articleOptions, setArticleOptions] = useState([]);
  const [loadModal, setLoadModal] = useState(false);
  const [loadForm, setLoadForm] = useState({ article_no: '', article_name: '', loaded_date: today(), remarks: '' });
  const [releaseModal, setReleaseModal] = useState(false);
  const [releaseTarget, setReleaseTarget] = useState(null);
  const [releaseForm, setReleaseForm] = useState({ released_date: today() });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const fetchData = () => {
    api.machines.get(id).then(({ data: res }) => {
      setData(res);
    }).catch((err) => {
      showError('Failed to load machine', extractError(err));
      navigate('/machines');
    });
  };

  useEffect(() => {
    setLoading(true);
    api.gatepasses.articles({ per_page: 1000 }).then(({ data }) => {
      const raw = data.articles || data || [];
      const list = Array.isArray(raw) ? raw : [];
      setArticleOptions(list.map((a) => ({
        value: a.article_no,
        label: a.name ? `${a.article_no} — ${a.name}` : a.article_no,
        article_no: a.article_no,
        name: a.name || '',
      })));
    }).catch(() => {});
    api.machines.get(id).then(({ data: res }) => {
      setData(res);
    }).catch((err) => {
      showError('Failed to load machine', extractError(err));
      navigate('/machines');
    }).finally(() => setLoading(false));
  }, [id, navigate]);

  const handleLoad = async () => {
    const errs = {};
    if (!loadForm.article_no) errs.article_no = 'Article No is required';
    if (!loadForm.loaded_date) errs.loaded_date = 'Loaded date is required';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      await api.machines.loadArticle(m.id, {
        article_no: loadForm.article_no,
        article_name: loadForm.article_name || undefined,
        loaded_date: loadForm.loaded_date,
        remarks: loadForm.remarks || undefined,
      });
      toast('success', '', 'Article loaded on machine');
      setLoadModal(false);
      setLoadForm({ article_no: '', article_name: '', loaded_date: today(), remarks: '' });
      fetchData();
    } catch (err) {
      showError('Failed to load article', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleRelease = async () => {
    const errs = {};
    if (!releaseForm.released_date) errs.released_date = 'Released date is required';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      await api.machines.releaseArticle(releaseTarget.id, releaseForm);
      toast('success', '', 'Article released');
      setReleaseModal(false);
      fetchData();
    } catch (err) {
      showError('Failed to release article', extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteArticle = async (row) => {
    const res = await confirmDelete(`article "${row.article_no}" record`);
    if (!res.isConfirmed) return;
    try {
      await api.machines.deleteArticle(row.id);
      toast('success', '', 'Record deleted');
      fetchData();
    } catch (err) {
      showError('Failed to delete record', extractError(err));
    }
  };

  if (loading) return <Loader text="Loading machine..." />;
  if (!data) return null;

  const m = data.machine || data;
  const activeAssignments = data.active_assignments || [];
  const pastAssignments = data.past_assignments || [];
  const recentStitches = data.recent_stitches || [];
  const activeArticles = data.active_articles || [];
  const pastArticles = data.past_articles || [];
  const stats = data.stats || {};

  return (
    <div className="page">
      <PageHeader
        title={m.name || 'Machine Detail'}
        subtitle={`${m.head_type || ''}-head machine — ${m.machine_code || ''}`}
        crumbs={[{ label: 'Production', to: '/machines' }, { label: m.name || 'Detail' }]}
        actions={
          <button className="btn btn-secondary" onClick={() => navigate('/machines')}><FiArrowLeft /> Back to Machines</button>
        }
      />

      <div className="grid-3" style={{ marginBottom: 18 }}>
        <div className="card stat-card">
          <div className="stat-body">
            <div className="stat-label">Total Stitches</div>
            <div className="stat-value">{Number(stats.total_stitches || 0).toLocaleString()}</div>
          </div>
        </div>
        <div className="card stat-card">
          <div className="stat-body">
            <div className="stat-label">Special Stitches</div>
            <div className="stat-value">{Number(stats.total_special_stitches || 0).toLocaleString()}</div>
          </div>
        </div>
        <div className="card stat-card">
          <div className="stat-body">
            <div className="stat-label">Active Operators</div>
            <div className="stat-value">{stats.active_operators || 0}</div>
          </div>
        </div>
      </div>

      <div className="grid-2" style={{ marginBottom: 18 }}>
        <div className="card">
          <div className="card-header"><div className="card-title">Machine Info</div></div>
          <div className="card-body">
            <table className="table" style={{ marginBottom: 0 }}>
              <tbody>
                <tr><td style={{ width: 180, fontWeight: 600 }}>Code</td><td>{m.machine_code || '—'}</td></tr>
                <tr><td style={{ fontWeight: 600 }}>Name</td><td>{m.name}</td></tr>
                <tr><td style={{ fontWeight: 600 }}>Head Type</td><td><span className="badge badge-blue">{m.head_type} Head</span></td></tr>
                <tr><td style={{ fontWeight: 600 }}>Rate / 1000</td><td>{formatMoney(m.rate_per_1000, 0)}</td></tr>
                <tr><td style={{ fontWeight: 600 }}>Fixed Below Threshold</td><td>{formatMoney(m.fixed_below_threshold, 0)}</td></tr>
                <tr><td style={{ fontWeight: 600 }}>Special Thread Price</td><td>{formatMoney(m.special_thread_price, 0)}</td></tr>
                <tr><td style={{ fontWeight: 600 }}>Threshold</td><td>{Number(m.threshold_stitches || 0).toLocaleString()}</td></tr>
                <tr><td style={{ fontWeight: 600 }}>Status</td><td><StatusBadge status={m.is_active ? 'active' : 'inactive'} /></td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <div className="card-title">Active Operators ({activeAssignments.length})</div>
          </div>
          <div className="card-body">
            {activeAssignments.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No operator currently assigned.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {activeAssignments.map((a, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 8, background: 'var(--bg)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div className="user-avatar" style={{ width: 34, height: 34, fontSize: 13 }}>
                        {(a.employee?.name || '?').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600 }}>{a.employee?.name || '—'}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{a.employee?.employee_type || ''}</div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {a.shift ? (
                        <span className="badge badge-green">{a.shift.name}</span>
                      ) : (
                        <span className="badge badge-gray">No Shift</span>
                      )}
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Since {a.assigned_date || '—'}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {pastAssignments.length > 0 && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card-header">
            <div className="card-title">Past Assignments ({pastAssignments.length})</div>
          </div>
          <div className="card-body">
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Employee</th><th>Type</th><th>Shift</th><th>Assigned Date</th><th>Released Date</th></tr>
                </thead>
                <tbody>
                  {pastAssignments.map((a, i) => (
                    <tr key={i}>
                      <td>{a.employee?.name || '—'}</td>
                      <td>{a.employee?.employee_type || '—'}</td>
                      <td>{a.shift ? <span className="badge badge-green">{a.shift.name}</span> : '—'}</td>
                      <td>{a.assigned_date || '—'}</td>
                      <td>{a.released_date || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-header">
          <div className="card-title">Articles on Machine ({activeArticles.length})</div>
          {hasPermission('manage-machine-articles') && (
            <button className="btn btn-primary btn-sm" onClick={() => { setErrors({}); setLoadModal(true); }}><FiPlus /> Load Article</button>
          )}
        </div>
        <div className="card-body">
          {activeArticles.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No article currently loaded on this machine.</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Article No</th><th>Article Name</th><th>Loaded Date</th><th style={{ textAlign: 'right' }}>Days</th><th>Remarks</th><th></th></tr>
                </thead>
                <tbody>
                  {activeArticles.map((a, i) => (
                    <tr key={i}>
                      <td><b>{a.article_no}</b></td>
                      <td>{a.article_name || '—'}</td>
                      <td>{formatDate(a.loaded_date)}</td>
                      <td style={{ textAlign: 'right' }}><b>{a.days ?? 0}</b> days on machine</td>
                      <td>{a.remarks || '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="actions">
                          {hasPermission('manage-machine-articles') && (
                            <button className="btn-icon" title="Release" onClick={() => { setReleaseTarget(a); setReleaseForm({ released_date: today() }); setErrors({}); setReleaseModal(true); }}><FiDownload /></button>
                          )}
                          {hasPermission('delete-machine-articles') && (
                            <button className="btn-icon danger" title="Delete" onClick={() => handleDeleteArticle(a)}><FiTrash2 /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {pastArticles.length > 0 && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card-header">
            <div className="card-title">Past Articles ({pastArticles.length})</div>
          </div>
          <div className="card-body">
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Article No</th><th>Article Name</th><th>Loaded Date</th><th>Released Date</th><th style={{ textAlign: 'right' }}>Days</th><th>Remarks</th><th></th></tr>
                </thead>
                <tbody>
                  {pastArticles.map((a, i) => (
                    <tr key={i}>
                      <td><b>{a.article_no}</b></td>
                      <td>{a.article_name || '—'}</td>
                      <td>{formatDate(a.loaded_date)}</td>
                      <td>{formatDate(a.released_date)}</td>
                      <td style={{ textAlign: 'right' }}><b>{a.days ?? 0}</b> days</td>
                      <td>{a.remarks || '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="actions">
                          {hasPermission('delete-machine-articles') && (
                            <button className="btn-icon danger" title="Delete" onClick={() => handleDeleteArticle(a)}><FiTrash2 /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {recentStitches.length > 0 && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Recent Production (last {recentStitches.length} entries)</div>
          </div>
          <div className="card-body">
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Date</th><th>Employee</th><th>Shift</th><th style={{ textAlign: 'right' }}>Stitches</th></tr>
                </thead>
                <tbody>
                  {recentStitches.map((s, i) => (
                    <tr key={i}>
                      <td>{formatDate(s.date)}</td>
                      <td>{s.employee?.name || '—'}</td>
                      <td>{s.employee?.shift?.name || '—'}</td>
                      <td style={{ textAlign: 'right' }}><b>{Number(s.stitch_count || 0).toLocaleString()}</b></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <Modal open={loadModal} onClose={() => { setErrors({}); setLoadModal(false); }} title={`Load Article — ${m.name || ''}`}
        footer={<>
          <button className="btn btn-secondary" onClick={() => setLoadModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleLoad} disabled={saving}>{saving ? 'Loading...' : 'Load Article'}</button>
        </>}
      >
        <div className="grid-2">
          <div className="form-group"><label className="form-label">Article No <span className="req">*</span></label>
            <SearchableSelect
              options={articleOptions}
              value={loadForm.article_no}
              onChange={(v) => {
                const opt = articleOptions.find((o) => o.value === v);
                setLoadForm({ ...loadForm, article_no: v, article_name: opt && opt.name ? opt.name : loadForm.article_name });
                if (errors.article_no) setErrors((p) => ({ ...p, article_no: '' }));
              }}
              isInvalid={!!errors.article_no}
              placeholder="-- Select article from gatepass --"
            />
            {errors.article_no && <div className="invalid-feedback">{errors.article_no}</div>}</div>
          <div className="form-group"><label className="form-label">Article Name</label>
            <input className="form-control" value={loadForm.article_name} onChange={(e) => setLoadForm({ ...loadForm, article_name: e.target.value })} /></div>
          <div className="form-group"><label className="form-label">Loaded Date <span className="req">*</span></label>
            <input className={`form-control ${errors.loaded_date ? 'is-invalid' : ''}`} type="date" value={loadForm.loaded_date}
              onChange={(e) => { setLoadForm({ ...loadForm, loaded_date: e.target.value }); if (errors.loaded_date) setErrors((p) => ({ ...p, loaded_date: '' })); }} />
            {errors.loaded_date && <div className="invalid-feedback">{errors.loaded_date}</div>}</div>
          <div className="form-group" style={{ gridColumn: '1 / -1' }}><label className="form-label">Remarks</label>
            <textarea className="form-control" rows={2} value={loadForm.remarks} onChange={(e) => setLoadForm({ ...loadForm, remarks: e.target.value })} /></div>
        </div>
      </Modal>

      <Modal open={releaseModal} onClose={() => { setErrors({}); setReleaseModal(false); }} title={`Release Article — ${releaseTarget?.article_no || ''}`}
        footer={<>
          <button className="btn btn-secondary" onClick={() => setReleaseModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleRelease} disabled={saving}>{saving ? 'Releasing...' : 'Release Article'}</button>
        </>}
      >
        <div className="form-group">
          <label className="form-label">Released Date <span className="req">*</span></label>
          <input className={`form-control ${errors.released_date ? 'is-invalid' : ''}`} type="date" value={releaseForm.released_date}
            onChange={(e) => { setReleaseForm({ released_date: e.target.value }); if (errors.released_date) setErrors((p) => ({ ...p, released_date: '' })); }} />
          {errors.released_date && <div className="invalid-feedback">{errors.released_date}</div>}
        </div>
        {releaseTarget && (
          <div className="alert-banner" style={{ marginTop: 10, fontSize: 13 }}>
            Loaded on: <b>{formatDate(releaseTarget.loaded_date)}</b>
          </div>
        )}
      </Modal>
    </div>
  );
}